import { Breakpoint, ModalProps, SxProps, Theme, useMediaQuery } from '@mui/material';
import Stack from '@mui/material/Stack';
import { styled } from '@mui/system';
import { CustomerAvatarBusiness, Exit } from '@o2/icons';
import CustomerAvatarResident from '@o2/icons/CustomerAvatarResident';
import { Box, Button, ButtonProps, Drawer, Link, LinkProps, Popper, Typography } from '@o2/ui';
import colors from '@o2/ui/theme/colors';
import { clsx } from 'clsx';
import { ElementType, FC, MouseEventHandler, ReactNode } from 'react';
import UserPopperLink from './UserPopperLink';
import { useUserPopperUtilityClasses } from './userPopperClasses';

export interface UserPopperElmIds {
  root: string;
  customerNameLabel: string;
  customerName: string;
  customerIcon: string;
  customerIdLabel: string;
  customerId: string;
  logoutBtn: string;
  logoutBtnInternal: string;
  btnMyServices: string;
  btnSettings: string;
  btnAddComment: string;
  btnHistory: string;
  btnBulkBilling: string;
  link: string;
}

export interface UserPopperCustomer {
  /**
   * User (customer) ID. (e.g. frantisek@mzourek.cz)
   */
  id?: string | null;

  /**
   *  User (customer) name. If it is null, only the UserID is displayed.
   */
  name?: string | null;

  /**
   * Switch between resident and business customer. (Toggles the display of the main UserPopper icon.)
   */
  type?: 'resident' | 'business';
}

export interface UserPopperProps<TLink extends ElementType = 'button'> {
  /**
   * Additional content (e.g. buttons) placed between the user account name and the logout button.
   */
  children?: ReactNode;

  /**
   * User popper ID
   */
  id?: string;

  /**
   * Open / close state
   */
  open?: boolean;

  /**
   * HTML element to which the UserPopper belongs. Probably a user button on the AppBar.
   */
  targetElm?: Element | null;

  /**
   * Handler for the Popper close event.
   */
  onClose: ModalProps['onClose'];

  /**
   * Switches UserPopper to the internal user view (e.g. changes the color and text of the logout button).
   */
  internal?: boolean;

  /**
   * Custom content of the logout button.
   */
  logoutBtnText?: ReactNode;

  /**
   * Logout button click handler.
   */
  onLogoutBtnClick?: MouseEventHandler;

  /**
   * UserPopperCustomer object
   */
  customer: UserPopperCustomer;

  /**
   * Custom text on the username label.
   */
  customerNameLabel?: ReactNode;

  /**
   * Custom text on the user id label.
   */
  customerIdLabel?: ReactNode;

  /**
   * Click handler for user ID. When set, the User ID is displayed as a link.
   */
  onCustomerIdClick?: MouseEventHandler;

  /**
   * List of footer links. (Use only <UserPopper.Link /> component!)
   */
  footerLinks?: ReactNode;

  /**
   * SX props
   */
  sx?: SxProps;

  /**
   * Breakpoint for switch between Popper and Drawer display
   */
  breakpoint?: Breakpoint;

  /**
   * Element IDs
   */
  elmIds?: Partial<UserPopperElmIds>;

  /**
   * Props passed to the individual subcomponents. Combine with `linkComponent` to avoid TS errors.
   */
  componentProps?: Partial<{
    logoutBtn: ButtonProps<TLink>;
    customerId: LinkProps<TLink>;
  }>;

  /**
   * What component to render this all links in this component as (inheriting props).
   * Useful when combining with e.g. react-router.
   *
   * The link elements include:
   * - logoutBtn
   * - customerId
   * - accessBtn
   * - logoutBtn
   * - footerLink
   */
  linkComponent?: TLink;
}

const UserPopperContainer = styled(Box, {
  name: 'O2UserPopper',
  slot: 'container',
})(({ theme }) => ({
  width: '328px',
  maxWidth: 'calc(100vw - 64px)',
  margin: theme.spacing(3, 2),
  textAlign: 'center',
}));

const UserPopperLinkContainer = styled(Box, {
  name: 'O2UserPopper',
  slot: 'linkContainer',
})<{ breakpoint: Breakpoint }>(({ theme, breakpoint }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: '360px',
  maxWidth: 'calc(100vw - 32px)',
  maxHeight: '235px',
  overflowY: 'auto',
  borderTop: `1px solid ${colors.solidGray['200']}`,
  [`${theme.breakpoints.down(breakpoint)}`]: {
    maxHeight: '100%',
  },
}));

type UserPopper = FC<UserPopperProps> & { Link: typeof UserPopperLink };

const templateId = '11.1.3';

/**
 * Default element IDs
 */
export const userPopperElmIds: UserPopperElmIds = {
  root: templateId,
  customerName: `${templateId}_011`,
  customerIcon: `${templateId}_021`,
  customerId: `${templateId}_016`,
  customerIdLabel: `${templateId}_016-label`,
  customerNameLabel: `${templateId}_011-label`,
  logoutBtn: `${templateId}_017`,
  logoutBtnInternal: `${templateId}_022`,
  btnMyServices: `${templateId}_018`,
  btnSettings: `${templateId}_012`,
  btnAddComment: `${templateId}_013`,
  btnHistory: `${templateId}_008`,
  btnBulkBilling: `${templateId}_024`,
  link: `${templateId}_015`,
};

/**
 * ### UserPopper component
 *
 * ####*Template: 11.1.3 - Hlavička*
 * For the correct display it is necessary pass to the component the target Element (props: targetElm) where the UserPopper should be displayed.
 * The component basically contains the user icon, name, id and logout button.
 * Additional buttons can be passed via children (you need to place them in your own Grid/Stack).
 * Popper supports displaying links in the popper footer. Their list can be passed via the *footerLinks* property.
 */
const UserPopper = <TLink extends ElementType = 'button'>({
  children,
  open = false,
  targetElm,
  onClose,
  internal,
  logoutBtnText,
  onLogoutBtnClick,
  customer,
  customerIdLabel = 'Vaše O2 ID',
  customerNameLabel = 'Zákazník',
  onCustomerIdClick,
  footerLinks,
  breakpoint = 'sm',
  elmIds = userPopperElmIds,
  componentProps,
  linkComponent,
  ...other
}: UserPopperProps<TLink>) => {
  const smDown = useMediaQuery((theme: Theme) => theme.breakpoints.down(breakpoint));
  const utilityClasses = useUserPopperUtilityClasses();

  const content = (
    <>
      <UserPopperContainer className={clsx(utilityClasses.container)}>
        <Stack direction="column" gap={3}>
          <Stack direction="column" gap={1}>
            <Box data-elm={elmIds.customerIcon}>
              {customer.type === 'resident' ? <CustomerAvatarResident /> : <CustomerAvatarBusiness />}
            </Box>
            {customer.name && (
              <Box>
                <Typography
                  data-elm={elmIds.customerNameLabel}
                  className={clsx(utilityClasses.customerNameLabel)}
                  variant="caption"
                  color="gray"
                >
                  {customerNameLabel}
                </Typography>
                <Typography data-elm={elmIds.customerName} className={clsx(utilityClasses.customerName)} variant="h4">
                  {customer.name}
                </Typography>
              </Box>
            )}
            {customer.id && (
              <Box>
                <Typography
                  data-elm={elmIds.customerIdLabel}
                  className={clsx(utilityClasses.customerIdLabel)}
                  variant="caption"
                  color="gray"
                >
                  {customerIdLabel}
                </Typography>
                <Typography
                  data-elm={elmIds.customerId}
                  className={clsx(utilityClasses.customerId)}
                  variant={customer.name ? 'body1' : 'h6'}
                >
                  {onCustomerIdClick || componentProps?.customerId ? (
                    <Link
                      onClick={onCustomerIdClick}
                      color={customer.name ? 'primary' : 'default'}
                      component={linkComponent}
                      {...componentProps?.customerId}
                    >
                      {customer.id}
                    </Link>
                  ) : (
                    customer.id
                  )}
                </Typography>
              </Box>
            )}
          </Stack>
          {children && <Box>{children}</Box>}
          <Box>
            <Button
              size="small"
              data-elm={internal ? elmIds.logoutBtnInternal : elmIds.logoutBtn}
              className={clsx(utilityClasses.logoutBtn)}
              variant="text"
              color={internal ? 'internal' : 'primary'}
              fullWidth
              startIcon={<Exit />}
              onClick={onLogoutBtnClick}
              {...(componentProps?.logoutBtn as ButtonProps<TLink>)}
              component={linkComponent as TLink}
            >
              {logoutBtnText ?? (internal ? 'Odhlásit kontaktní osobu' : 'Odhlásit')}
            </Button>
          </Box>
        </Stack>
      </UserPopperContainer>
      {footerLinks && (
        <UserPopperLinkContainer breakpoint={breakpoint} className={clsx(utilityClasses.linkContainer)}>
          {footerLinks}
        </UserPopperLinkContainer>
      )}
    </>
  );

  if (smDown) {
    return (
      <Drawer
        anchor="right"
        open={open}
        onClose={() => {
          onClose && onClose({}, 'backdropClick');
        }}
      >
        {content}
      </Drawer>
    );
  }

  return (
    <Popper
      open={open}
      anchorEl={targetElm}
      PaperProps={{
        mt: '-6px',
      }}
      onClickAway={() => {
        onClose && onClose({}, 'backdropClick');
      }}
      className={clsx(utilityClasses.root)}
      data-elm={elmIds.root}
      {...other}
    >
      {content}
    </Popper>
  );
};

UserPopper.Link = UserPopperLink;

export default UserPopper;
